/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
    Template,
    TemplateFilters,
    TemplateFolder,
    TemplateVersion,
} from 'views/retainerAgreement/types';
import { logout } from '../auth/auth-middleware';
import {
    loadTemplates,
    loadTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    loadTemplateVersions,
    loadFolders,
    loadTemplatesForSelection,
    loadApplicationTemplate,
} from './templates.middleware';
import { isFeatureNotEnabledError } from 'utils/features';

type TemplatesState = {
    list: Template[];
    selectedTemplate: Template | null;
    filters: TemplateFilters;
    folders: TemplateFolder[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        per_page: number;
        total: number;
        pages: number;
    };
    // For template editor
    currentTemplate: Template | null;
    versions: TemplateVersion[];
    isLoadingVersions: boolean;
    // For OnboardingAgreement
    templatesForSelection: Template[];
    isLoadingSelection: boolean;
    associatedTemplate: Template | null;
    // For inline template CRUD in agreement flow
    selectedForAgreement: string | null;
    showInlineCreator: boolean;
    showInlineEditor: boolean;
    showChangeWarning: boolean;
    templateBeingEdited: Template | null;
    templateBeingDeleted: Template | null;
};

const initialFilters: TemplateFilters = {
    search: '',
    type: 'all',
    country: '',
    program: '',
    status: 'all',
    hasLogo: 'all',
    pricingModel: 'all',
};

const initialState: TemplatesState = {
    list: [],
    selectedTemplate: null,
    filters: initialFilters,
    folders: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        per_page: 20,
        total: 0,
        pages: 0,
    },
    currentTemplate: null,
    versions: [],
    isLoadingVersions: false,
    templatesForSelection: [],
    isLoadingSelection: false,
    associatedTemplate: null,
    selectedForAgreement: null,
    showInlineCreator: false,
    showInlineEditor: false,
    showChangeWarning: false,
    templateBeingEdited: null,
    templateBeingDeleted: null,
};

export const templatesSlice = createSlice({
    name: 'app/templates',
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<Partial<TemplateFilters>>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        setSelectedTemplate: (state, action: PayloadAction<Template | null>) => {
            state.selectedTemplate = action.payload;
        },
        resetTemplates: (state) => {
            state.list = [];
            state.selectedTemplate = null;
            state.currentTemplate = null;
            state.versions = [];
            state.error = null;
            state.isLoading = false;
            state.filters = initialFilters;
            state.pagination = {
                page: 1,
                per_page: 20,
                total: 0,
                pages: 0,
            };
        },
        clearCurrentTemplate: (state) => {
            state.currentTemplate = null;
            state.versions = [];
        },
        // Inline template CRUD actions for agreement flow
        selectTemplateForAgreement: (state, action: PayloadAction<string | null>) => {
            state.selectedForAgreement = action.payload;
        },
        clearTemplateSelection: (state) => {
            state.selectedForAgreement = null;
        },
        setShowInlineCreator: (state, action: PayloadAction<boolean>) => {
            state.showInlineCreator = action.payload;
        },
        setShowInlineEditor: (state, action: PayloadAction<boolean>) => {
            state.showInlineEditor = action.payload;
        },
        setTemplateBeingEdited: (state, action: PayloadAction<Template | null>) => {
            state.templateBeingEdited = action.payload;
        },
        setTemplateBeingDeleted: (state, action: PayloadAction<Template | null>) => {
            state.templateBeingDeleted = action.payload;
        },
        setShowChangeWarning: (state, action: PayloadAction<boolean>) => {
            state.showChangeWarning = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Load Templates
            .addCase(loadTemplates.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loadTemplates.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.data?.data) {
                    state.list = action.payload.data.data.templates || [];
                    state.pagination = {
                        page: action.payload.data.data.page || 1,
                        per_page: action.payload.data.data.per_page || 20,
                        total: action.payload.data.data.total || 0,
                        pages: action.payload.data.data.pages || 0,
                    };
                }
            })
            .addCase(loadTemplates.rejected, (state, action) => {
                state.isLoading = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string' ? errorPayload : 'Failed to load templates');
            })
            // Load Template
            .addCase(loadTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loadTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.data?.data) {
                    state.currentTemplate = action.payload.data.data;
                    state.versions = action.payload.data.data.versions || [];
                }
            })
            .addCase(loadTemplate.rejected, (state, action) => {
                state.isLoading = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string' ? errorPayload : 'Failed to load template');
            })
            // Create Template
            .addCase(createTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.data?.data) {
                    state.currentTemplate = action.payload.data.data;
                }
            })
            .addCase(createTemplate.rejected, (state, action) => {
                state.isLoading = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string' ? errorPayload : 'Failed to create template');
            })
            // Update Template
            .addCase(updateTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                if (action.payload?.data?.data) {
                    state.currentTemplate = action.payload.data.data;
                    // Update in list if exists
                    const index = state.list.findIndex((t) => t.id === action.payload.data.data.id);
                    if (index !== -1) {
                        state.list[index] = action.payload.data.data;
                    }
                }
            })
            .addCase(updateTemplate.rejected, (state, action) => {
                state.isLoading = false;
                const errorPayload = action.payload as { error?: string } | string | undefined;
                state.error =
                    (typeof errorPayload === 'object' && errorPayload?.error) ||
                    (typeof errorPayload === 'string' ? errorPayload : 'Failed to update template');
            })
            // Delete Template
            .addCase(deleteTemplate.fulfilled, (state, action) => {
                state.list = state.list.filter((t) => t.id !== action.payload.toString());
                if (state.currentTemplate?.id === action.payload.toString()) {
                    state.currentTemplate = null;
                }
            })
            // Load Template Versions
            .addCase(loadTemplateVersions.pending, (state) => {
                state.isLoadingVersions = true;
            })
            .addCase(loadTemplateVersions.fulfilled, (state, action) => {
                state.isLoadingVersions = false;
                if (action.payload?.data?.data) {
                    state.versions = action.payload.data.data;
                }
            })
            .addCase(loadTemplateVersions.rejected, (state) => {
                state.isLoadingVersions = false;
            })
            // Load Folders
            .addCase(loadFolders.fulfilled, (state, action) => {
                if (action.payload?.data?.data) {
                    state.folders = action.payload.data.data;
                }
            })
            // Load Templates for Selection
            .addCase(loadTemplatesForSelection.pending, (state) => {
                state.isLoadingSelection = true;
            })
            .addCase(loadTemplatesForSelection.fulfilled, (state, action) => {
                state.isLoadingSelection = false;
                if (action.payload?.data?.data) {
                    // Convert id to string for each template
                    state.templatesForSelection = (action.payload.data.data || []).map(
                        (template: any) => ({
                            ...template,
                            id: template.id.toString(),
                        })
                    );
                }
            })
            .addCase(loadTemplatesForSelection.rejected, (state) => {
                state.isLoadingSelection = false;
            })
            // Load Application Template
            .addCase(loadApplicationTemplate.fulfilled, (state, action) => {
                if (action.payload?.data?.data) {
                    state.associatedTemplate = action.payload.data.data;
                } else {
                    state.associatedTemplate = null;
                }
            })
            // Logout
            .addCase(logout.fulfilled, () => {
                return initialState;
            })
            .addMatcher(
                (action): action is { type: string; payload: unknown } =>
                    typeof action.type === 'string' &&
                    action.type.startsWith('api/retainer-templates') &&
                    action.type.endsWith('/rejected'),
                (state, action) => {
                    if (isFeatureNotEnabledError(action.payload)) {
                        state.error = null;
                    }
                }
            );
    },
});

export const {
    setFilters,
    setSelectedTemplate,
    resetTemplates,
    clearCurrentTemplate,
    selectTemplateForAgreement,
    clearTemplateSelection,
    setShowInlineCreator,
    setShowInlineEditor,
    setTemplateBeingEdited,
    setTemplateBeingDeleted,
    setShowChangeWarning,
} = templatesSlice.actions;
export default templatesSlice.reducer;
